import { RotateCw, Trash2, Check, Film } from "lucide-react";

/**
 * Pipeline "Recent" history strip — 3-col grid of tiles with select-mode + send-to-storyboard.
 * State (history, historySelectMode, historySelected) lives in Studio.jsx; this is a pure presentation component.
 */
export const PipelineHistoryStrip = ({
  history,
  historySelectMode,
  historySelected,
  onStartSelect,
  onCancelSelect,
  onClearAll,
  onClearSelected,
  onToggleSelect,
  onRestore,
  onSendToStoryboard,
}) => {
  if (!history || history.length === 0) return null;
  return (
    <div className="mt-6" data-testid="pipeline-history">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="font-mono-hy text-[10px] text-white flex items-center gap-2">
          <RotateCw className="w-3 h-3" /> // Recent · {history.length}
          {historySelectMode && (
            <span className="text-[#00E5FF]">· {historySelected.size} selected</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {historySelectMode ? (
            <>
              <button
                onClick={onClearSelected}
                disabled={historySelected.size === 0}
                data-testid="history-clear-selected-btn"
                className="font-mono-hy text-[9px] px-2.5 py-1 rounded-full border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Clear Selected
              </button>
              <button
                onClick={onCancelSelect}
                data-testid="history-cancel-select-btn"
                className="font-mono-hy text-[9px] px-2.5 py-1 rounded-full border border-[#F2C94C]/20 text-white hover:text-[#F2C94C]"
              >Done</button>
            </>
          ) : (
            <>
              <button
                onClick={onStartSelect}
                data-testid="history-select-mode-btn"
                className="font-mono-hy text-[9px] px-2.5 py-1 rounded-full border border-[#F2C94C]/20 text-white hover:text-[#F2C94C] flex items-center gap-1"
              >
                <Check className="w-3 h-3" /> Select
              </button>
              <button
                onClick={onClearAll}
                data-testid="history-clear-all-btn"
                className="font-mono-hy text-[9px] px-2.5 py-1 rounded-full border border-[#E27D60]/30 text-[#E27D60] hover:bg-[#E27D60]/10 flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Clear All
              </button>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {history.map((h, i) => {
          const thumb = h.image_url || h.views?.front?.image_url;
          const selected = historySelected.has(h.generation_id);
          return (
            <div key={h.generation_id} className="relative group" data-testid={`history-tile-${i}`}>
              <button
                onClick={() => historySelectMode ? onToggleSelect(h.generation_id) : onRestore(h)}
                data-testid={`history-item-${i}`}
                className={`w-full aspect-video rounded-xl overflow-hidden border transition-all ${
                  historySelectMode && selected
                    ? "border-[#00E5FF] ring-2 ring-[#00E5FF]/50"
                    : "border-[#F2C94C]/15 hover:border-[#F2C94C]/50"
                }`}
              >
                {h.turntable_video_url ? (
                  <video src={h.turntable_video_url} muted loop playsInline autoPlay className="w-full h-full object-cover pointer-events-none" />
                ) : h.video_url ? (
                  <video src={h.video_url} muted loop playsInline autoPlay className="w-full h-full object-cover pointer-events-none" />
                ) : thumb ? (
                  <img src={thumb} alt="" className="w-full h-full object-cover pointer-events-none" />
                ) : (<div className="w-full h-full bg-[#120E15]" />)}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/95 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-1.5 left-2 right-2 font-mono-hy text-[8px] text-white uppercase truncate pointer-events-none">
                  {h.type === "turnaround" ? "◇ 4·VIEWS" : h.video_url ? "▶ VIDEO" : "◎ IMAGE"} · {h.prompt || "—"}
                </div>
                {historySelectMode && (
                  <div className={`absolute top-1.5 left-1.5 w-5 h-5 rounded-full flex items-center justify-center border-2 pointer-events-none ${selected ? "bg-[#00E5FF] border-[#00E5FF]" : "bg-[#0A0A0A]/70 border-white/50"}`}>
                    {selected && <Check className="w-3 h-3 text-[#0A0A0A]" />}
                  </div>
                )}
              </button>
              {!historySelectMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); onSendToStoryboard(h); }}
                  data-testid={`history-to-storyboard-${i}`}
                  title="Send to Storyboard as a new scene"
                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#0A0A0A]/85 border border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C] hover:text-[#0A0A0A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Film className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
